/**************************************************
 * utils/dashApi.js
 *
 * @author  LSJ
 * @since   1.0 (2025-02-28)
 * @version 1.0
 **************************************************/
import { del, get, post, put } from "@utils/axios_api";

/**
 * fetchData
 *
 * 주어진 엔드포인트와 파라미터 객체를 기반으로 데이터를 가져옵니다.
 * 백엔드 응답은 response.data에 담겨 옵니다.
 *
 * @param   {string}    endpoint    API 엔드포인트 (예: "/user/dash/current")
 * @param   {object}    params      API 호출에 사용할 파라미터 객체 (예: { compCode, otherParam })
 * @param   {string}    restType    RestFul 종류 (예: "get", "put")
 * @param   {object}    data        RestFul 종류 (예: "get", "put")
 * @returns {Promise}               API 결과 데이터
 */
export const fetchData = (endpoint, params, restType, data, config = {}) => {
  if (restType === "get") {
    return get(endpoint, { params, ...config }).then((data) => (data !== undefined ? data : []));
  } else if (restType === "post") {
    return post(endpoint, data, { params, ...config }).then((data) => (data !== undefined ? data : []));
  } else if (restType === "put") {
    return put(endpoint, data, { params, ...config }).then((data) => (data !== undefined ? data : []));
  } else if (restType === "del") {
    return del(endpoint, { params, ...config }).then((data) => (data !== undefined ? data : []));
  }
};

/**
 * gridMergeDataByKey
 *
 * data 배열에서 연속된 동일 key 값을 가진 행에 대해, 첫 번째 행에는 그룹 개수를, 나머지 행에는 0을 부여하여 rowSpan 값을 설정한 배열을 반환합니다.
 *
 * @param   {Array}   data  원본 데이터 배열 (각 요소는 key 속성을 포함)
 * @param   {string}  key   그룹핑할 속성 이름 (예: "procName", "hasMgr")
 * @returns {Array}         rowSpan 속성이 추가된 새로운 배열
 */
export const gridMergeDataByKey = (data, key) => {
  // 결과를 담을 배열 초기화
  const result = [];

  // 데이터 배열 전체를 순회합니다.
  let i = 0;
  while (i < data.length) {
    // 현재 행의 key 값 저장
    const currentValue = data[i][key];
    // 그룹 내 행 개수 초기화 (최소 1)
    let count = 1;
    let j = i + 1;
    // 다음 행부터 같은 key 값을 가진 행의 개수를 센다.
    while (j < data.length && data[j][key] === currentValue) {
      count++;
      j++;
    }
    // 그룹의 첫 번째 행에는 rowSpan을 count로, 나머지 행에는 0을 설정한다.
    for (let k = i; k < j; k++) {
      result[k] = { ...data[k], rowSpan: k === i ? count : 0 };
    }
    // 그룹이 끝난 후, 다음 그룹의 시작 인덱스로 이동
    i = j;
  }
  return result;
};

/**
 * filterRoleList
 *
 * roleList와 roleEmpInfo 배열을 회사 규모(50인 이상/이하)에 따라 필터링하여 결과를 반환합니다.
 * - 50인 이상: 대표이사, 근로자대표, 안전보건관리책임자, 안전관리자, 보건관리자
 * - 50인 이하: 대표이사, 근로자대표, 안전보건관리담당자
 * 
 * @param   {Array} roleList    역할 객체들의 배열 (각 객체는 최소한 usrDfn1과 comdCode 속성을 포함)
 * @param   {Array} roleEmpInfo 역할 담당자 객체들의 배열 (각 객체는 최소한 role과 empName 속성을 포함)
 * @param   {string} sizeCategory 회사 규모 ("1": 50인 미만, "2": 50인 이상)
 * @returns {Object}            필터링된 roleList와 roleEmpInfo 배열
 */
export const filterRoleList = (roleList, roleEmpInfo, sizeCategory) => {
  
  // 50인 이상/이하에 따른 역할명 정의
  const is50OrMore = sizeCategory === "2"; // "2"가 50인 이상
  
  let allowedRoles = [];
  
  if (is50OrMore) {
    // 50인 이상: 대표이사, 근로자대표, 안전보건관리책임자, 안전관리자, 보건관리자
    allowedRoles = [
      "대표 이사",
      "근로자 대표",
      "안전보건 관리책임자",
      "안전관리자",
      "보건관리자"
    ];
  } else {
    // 50인 이하: 대표이사, 근로자대표, 안전보건관리담당자
    allowedRoles = [
      "대표 이사",
      "근로자 대표",
      "안전보건 관리담당자"
    ];
  }
  
  // roleList에서 허용된 역할만 필터링
  const filteredRoleList = roleList.filter((item) => {
    // 허용된 역할만 포함 (담당자 유무와 관계없이)
    const isAllowedRole = allowedRoles.includes(item.comdName);
    return isAllowedRole;
  });

  // filteredRoleList의 각 항목의 comdCode 값을 배열로 추출합니다.
  const filteredComdCodes = filteredRoleList.map((item) => item.comdCode);

  // roleEmpInfo에서 role이 filteredComdCodes에 포함된 항목만 필터링합니다.
  const filteredRoleEmpInfo = roleEmpInfo.filter((item) => filteredComdCodes.includes(item.role));

  return { filteredRoleList, filteredRoleEmpInfo };
};

/**
 * getCurrentDate
 *
 * 현재 날짜를 지정된 구분 문자로 연결하여 반환합니다.
 * 기본 구분 문자는 . 입니다.
 *
 * @param   {string}  [delimiter='.'] (예: '-', '/', '.')
 * @returns {string}
 */
export const getCurrentDate = (delimiter = ".") => {
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = (date.getMonth() + 1).toString().padStart(2, "0");
  const dd = date.getDate().toString().padStart(2, "0");
  return `${yyyy}${delimiter}${mm}${delimiter}${dd}`;
};
